import { useLang } from 'hooks/lang';

import {
  Column,
  LabeledTextarea,
  Label,
  ResponsiveOverlay,
  Button,
  Row,
  IconButton,
} from 'components/Blocks';
import { DecimalInput } from 'components/Inputs';

import { getID } from 'utils/id';
import { calculateInvoiceValues, validateDiscount } from './utils';

import { FormPieceProps, InvoiceControlCreation } from './types';
import { ChatIcon, DollarBillIcon, PencilIcon, TrashIcon } from 'components/Icons';
import {
  DiscountRequestTypeSelect,
  DiscountRequestTypeSelectProps,
} from 'pages/Invoice/components';
import { useState } from 'react';
import { formatCurrency } from 'utils/text';

export function DiscountRequest({
  state,
  dispatch,
  currency,
}: Omit<FormPieceProps, 'isOpen'> & { currency: string }) {
  const { t } = useLang();

  const [localRequest, setRequest] = useState<FormPieceProps['state']['discountRequest'] | null>();

  const handleTypeChange = (values: Parameters<DiscountRequestTypeSelectProps['update']>[0]) => {
    setRequest(
      (old) =>
        (values?.salesDefined
          ? {
              ...old,
              ...values,
              percentage: '0.00',
              value: '0.00',
              salesDefined: true,
            }
          : {
              ...old,
              type: values.type,
              salesDefined: false,
            }) as InvoiceControlCreation['discountRequest'],
    );
  };

  const request = localRequest ?? state.discountRequest;

  const hasDiscount = !!state.discountRequest;
  const salesDefined = !!request?.salesDefined;

  const isPercent = request?.type === 'PERCENTAGE';

  const { total, appliedDiscount } = calculateInvoiceValues({
    ...state,
    discountRequest: localRequest || state.discountRequest,
  });

  const valueIsOK = salesDefined
    ? true
    : isPercent
      ? validateDiscount(request, total)
      : total >= Number(request?.value || 0);

  const isDisabled = !localRequest || !valueIsOK || !localRequest.requestComment;

  const syncUp = () => {
    if (isDisabled) return;

    dispatch({ discountRequest: localRequest! });
    setRequest(null);
  };

  return (
    <Column>
      <Column gap="2">
        <Label htmlFor="req-disc">
          {t('discount')}

          {hasDiscount && (
            <>
              <IconButton onClick={() => setRequest(state.discountRequest!)} className="-mr-2">
                <PencilIcon size={10} />
              </IconButton>

              <IconButton onClick={() => dispatch(({ discountRequest: _, ...rest }) => rest)}>
                <TrashIcon size={10} />
              </IconButton>
            </>
          )}
        </Label>

        {hasDiscount ? (
          <Column relative gap="2" className="pl-3">
            <div className="absolute bottom-0 left-0 top-0 w-[1.5px] rounded-full bg-border" />

            <Row gap="2">
              <DollarBillIcon />

              {state.discountRequest?.salesDefined ? (
                <span className="text-sm italic text-success">{t('sales-defined')}</span>
              ) : state.discountRequest!.type === 'PERCENTAGE' ? (
                <span>
                  {state.discountRequest?.percentage}
                  <span className="ml-1.5 text-sm text-success">%</span>

                  <span className="ml-1.5 text-xxs italic">
                    ({formatCurrency(appliedDiscount, currency)})
                  </span>
                </span>
              ) : (
                <span>
                  {state.discountRequest?.value}
                  <span className="ml-1.5 text-xs font-medium text-success">{currency}</span>
                </span>
              )}
            </Row>

            <Row gap="2">
              <ChatIcon />
              <span className="text-sm italic text-muted-foreground/60">
                {state.discountRequest?.requestComment}
              </span>
            </Row>
          </Column>
        ) : (
          <Button
            variant="ghost"
            className="-ml-1 w-fit px-1"
            onClick={() =>
              setRequest(
                state.discountRequest || {
                  percentage: '',
                  requestComment: '',
                  type: 'PERCENTAGE',
                  salesDefined: false,
                  value: '',
                },
              )
            }
          >
            <DollarBillIcon />
            {t('request')}
          </Button>
        )}

        <ResponsiveOverlay
          width="min(90vw, 480px)"
          gap="6"
          isOpen={!!localRequest}
          close={() => setRequest(null)}
        >
          <span className="-mt-2" />

          <DiscountRequestTypeSelect
            update={handleTypeChange}
            type={request?.type || ''}
            salesDefined={salesDefined}
            addSalesDefinedOption
          />

          <DecimalInput
            disabled={salesDefined}
            className={!valueIsOK && !salesDefined ? 'border-destructive' : ''}
            name={`discount-${getID()}`}
            value={isPercent ? request?.percentage : request?.value}
            onChangeDirect={(newValue) =>
              setRequest((old) => ({
                ...old!,
                percentage: newValue,
                value: newValue,
              }))
            }
          />

          <LabeledTextarea
            value={localRequest?.requestComment || ''}
            label={t('why-is-this-discount-necessary')}
            onChange={({ target }) =>
              setRequest((old) => ({ ...old!, requestComment: target.value }))
            }
          />

          <Button onClick={syncUp} disabled={isDisabled}>
            {t('confirm')}
          </Button>
        </ResponsiveOverlay>
      </Column>
    </Column>
  );
}
